import {type GatewayConfiguration, GatewayEligibility} from '../models/GatewayConfiguration';
import {startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {type GatewayEligibilityContext, PaymentConfiguration, setSelectedPaymentGateway} from '../reducers/paymentConfigurationReducer';
import {store} from '../store';
import {getLocaleText} from '../util/translation';
import {requestCartCalculation} from '../payment/order';
import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {startPaymentIntegrationInitialization} from '../payment/lazy-integrations';

import ellipsisImageURL from '../../img/dot-dot-dot.svg';
import helpImageURL from '../../img/property-help.svg';
import {setupCurrencyFallbackEvents} from './currencySwitch';

function initPaymentGatewaySelector() {
	setupPrimaryOptionsEvents();
	setupSecondaryOptionsEvents();
	setupCurrencyFallbackEvents();

	store.subscribe(() => {
		const sortedEligibilityContext = PaymentConfiguration.sortGatewaysByEligibility();
		const unmountedElements = (selector: string) => $qsAll<HTMLDivElement>(selector).reduce<Record<string, HTMLDivElement>>((acc, $el) => {
			const gatewayId = $el.dataset['gateway'] ?? '';
			$el.remove();

			acc[gatewayId] = $el;

			return acc;
		}, {});

		// Render/rerender primary gateway options
		const unmountedPrimaryElements = unmountedElements('.pp-pms .primary-option[data-gateway]');
		for (const context of sortedEligibilityContext) {
			renderPrimaryOption(context, unmountedPrimaryElements[context.config.gatewayId]);
		}

		// Render/rerender primary more option
		renderPrimaryMoreOption();

		// Render/rerender secondary gateway options
		const unmountedSecondaryElements = unmountedElements('.pp-pms .secondary-option[data-gateway]');
		for (const context of sortedEligibilityContext) {
			renderSecondaryOption(context, unmountedSecondaryElements[context.config.gatewayId]);
		}

		// Render/rerender gateway descriptions
		for (const context of sortedEligibilityContext) {
			renderGatewayDescription(context);
		}

		renderEligibilityDescription(PaymentConfiguration.gatewayConfig(PaymentConfiguration.selectedGateway()));
	});
}

function setupPrimaryOptionsEvents() {
	$qs('.pp-pms')?.addEventListener('click', async e => {
		const $target = e.target as HTMLElement | null;
		const $pmType = $target?.closest<HTMLDivElement>('.pp-pm-type:not(.pp-more-options)');

		if (!$pmType) {
			return;
		}

		const gatewayId = $pmType.dataset['gateway'] ?? '';

		startPaymentIntegrationInitialization('gateway-select');
		store.dispatch(startModalLoading());
		try {
			store.dispatch(setSelectedPaymentGateway(gatewayId));
			await requestCartCalculation('push', 'payment-selector:change');
		} finally {
			store.dispatch(stopModalLoading());
		}
	});

	$qs('.pp-pms')?.addEventListener('keydown', e => {
		if (e.key === 'Enter') {
			const $target = e.target as HTMLDivElement;
			$target.click();
		}
	});
}

function renderPrimaryOption(context: GatewayEligibilityContext, $el: HTMLDivElement | undefined) {
	const {config, eligibility, displayIndex} = context;
	const isSelected = PaymentConfiguration.selectedGateway() === config.gatewayId;
	const isHidden = !eligibility || displayIndex === undefined || displayIndex >= 3;

	if ($el) {
		$el.classList.toggle('selected', isSelected);
		$el.classList.toggle('hide', isHidden);
		$el.classList.toggle('pp-pm-alert', eligibility !== GatewayEligibility.Eligible);

		// Add element back to the DOM in the new order
		$qs('.pp-pm-type.pp-more-options')?.insertAdjacentElement('beforebegin', $el);
	} else {
		$qs('.pp-pm-type.pp-more-options')?.insertAdjacentHTML('beforebegin', `
			<div class="pp-pm-type primary-option ${isSelected ? 'selected' : ''} ${isHidden ? 'hide' : ''}" tabindex="0" role="button" data-gateway="${config.gatewayId}" >
				<img class="pp-pm-full-badge" src="${config.assets.badge.src}" draggable="false">
				<div class="pp-pm-type-content" style="display:flex;gap:4px">
					<span>${config.name}</span>
				</div>
			</div>`,
		);
	}
}

function renderPrimaryMoreOption() {
	const isHidden = PaymentConfiguration.eligibleGatewayCount() <= 3;

	const $el = $qs<HTMLElement>('.pp-pm-type.pp-more-options');
	if ($el) {
		$el.classList.toggle('hide', isHidden);
	} else {
		$qs('.pp-pms div.header')?.insertAdjacentHTML('beforeend', `
			<div class="pp-pm-type pp-more-options ${isHidden ? 'hide' : ''}" tabindex="0" role="button">
				<img class="pp-pm-more-options" src="${ellipsisImageURL}" draggable="false">
				<span class="pp-pm-more-container hide">
					<ul class="pp-pm-more"></ul>
				</span>
				<span class="pp-question-mark hide">
					<img class="pp-pm-help-badge" src="${helpImageURL}">
				</span>
			</div>`,
		);
	}
}

function setupSecondaryOptionsEvents() {
	// Hide secondary options overlay if clicking anywhere outside of it.
	$qs('body')?.addEventListener('click', e => {
		const $target = e.target as HTMLElement | null;
		const $pmOptionSelector = $target?.closest<HTMLElement>('.pp-pm-more-container');

		if (!$pmOptionSelector) {
			$qsAll('.secondary-option').forEach($el => {
				$el.setAttribute('tabindex', '-1');
			});
			$qs('.pp-pm-more-container')?.classList.add('hide');
		}
	});

	$qs('.pp-pms')?.addEventListener('click', async e => {
		const $target = e.target as HTMLElement | null;
		const $pmType = $target?.closest<HTMLDivElement>('.pp-pm-type.pp-more-options');
		if (!$pmType) {
			$qsAll('.secondary-option').forEach($el => {
				$el.setAttribute('tabindex', '-1');
			});
			$qs('.pp-pm-more-container')?.classList.add('hide');
			return;
		}

		$pmType.querySelector('.pp-pm-more-container')?.classList.toggle('hide');
		if (!$pmType.querySelector('.pp-pm-more-container')?.classList.contains('hide')) {
			$qsAll('.secondary-option').forEach($el => {
				$el.setAttribute('tabindex', '0');
			});
		}

		e.preventDefault();
		e.stopPropagation();

		const $option = $target?.closest<HTMLLIElement>('li[data-gateway]');
		if (!$option) {
			return;
		}

		const gatewayId = $option?.dataset['gateway'];

		if (!gatewayId) {
			return;
		}

		startPaymentIntegrationInitialization('gateway-select');
		store.dispatch(startModalLoading());
		try {
			store.dispatch(setSelectedPaymentGateway(gatewayId));
			await requestCartCalculation('push', 'payment-selector:change');
		} finally {
			store.dispatch(stopModalLoading());
		}
	});
}

function renderSecondaryOption(context: GatewayEligibilityContext, $el: HTMLDivElement | undefined) {
	const {config, eligibility, displayIndex} = context;
	const isHidden = !eligibility || displayIndex === undefined || displayIndex <= 2;

	if ($el) {
		$el.classList.toggle('hide', isHidden);
		$el.querySelector('img')?.setAttribute('src', config.assets.badge.src);

		const $span = $el.querySelector('span');
		if ($span) {
			$span.innerHTML = config.name;
		}

		// Add element back to the DOM in the new order
		$qs('.pp-pm-more')?.insertAdjacentElement('beforeend', $el);
	} else {
		$qs('.pp-pm-more')?.insertAdjacentHTML('beforeend', /* html */ `
			<li class="secondary-option ${isHidden ? 'hide' : ''}" data-gateway="${config.gatewayId}" role="button" tabindex="-1">
				<img class="pp-more-option-badge" src="${config.assets.badge.src}" draggable="false">
				<span>${config.name}</span>
			</li>`,
		);
	}
}

function renderGatewayDescription(context: GatewayEligibilityContext) {
	const isSelected = PaymentConfiguration.selectedGateway() === context.config.gatewayId;
	const isHidden = !isSelected || context.eligibility !== GatewayEligibility.Eligible;

	const $el = $qs<HTMLDivElement>(`.pp-pm-saved-option[data-gateway="${context.config.gatewayId}"]`);
	if ($el) {
		$el.classList.toggle('selected', isSelected);
		$el.parentElement?.classList.toggle('hide', isHidden);
	} else {
		$qs('.pp-pms div.body')?.insertAdjacentHTML('beforeend', `
			<div class="${isHidden ? 'hide' : ''}">
				<div class="pp-pm-saved-option ${isSelected ? 'selected' : ''}" data-gateway="${context.config.gatewayId}">
					${context.config.description}
				</div>
			</div>`,
		);
	}
}

function renderEligibilityDescription(config: GatewayConfiguration | null) {
	const $el = $qs<HTMLDivElement>('.currency-fallback-description');

	if (!config) {
		$el?.classList.add('hide');
		return;
	}

	const eligibility = PaymentConfiguration.eligibleGateway(config.gatewayId);
	const eligibilityDetails = PaymentConfiguration.eligibleGatewayDetails(config.gatewayId) ?? {};
	const eligibilityExplanations = Object.values(eligibilityDetails).map(detail => detail.explanation).join('<br/>');

	const isHidden = eligibility === GatewayEligibility.Eligible;

	const defaultCurrency = eligibilityDetails.currency?.fallback_option ?? '';

	if (isHidden || !eligibilityExplanations) {
		$el?.classList.add('hide');
		return;
	}

	if ($el) {
		$el.classList.remove('hide');
		$el.querySelector('img')?.setAttribute('src', config.assets?.title?.src ?? config?.assets.badge.src);
		$el.querySelectorAll('.name').forEach($span => {
			$span.innerHTML = config.name ?? '';
		});
		$el.querySelectorAll('.explanations').forEach($span => {
			$span.innerHTML = eligibilityExplanations ?? '';
		});

		$el.querySelector('button')?.classList.toggle('hide', !defaultCurrency);
		$el.querySelector('button')?.setAttribute('data-gateway', config.gatewayId);
		$el.querySelector('button')?.setAttribute('data-currency', defaultCurrency ?? '');
		$el.querySelectorAll('button .currency').forEach($span => {
			$span.innerHTML = defaultCurrency ?? '';
		});
	} else {
		$qs('.pp-pms div.body')?.insertAdjacentHTML('beforeend', `
			<div class="pp-pm-saved-option currency-fallback-description" >
				<img style="display: block; text-align: left; height: 1.5rem; " src="${config.assets?.title?.src ?? config?.assets.badge.src}">
				<p style="text-align: left; margin: 0.5rem 0;">
					<span class="name">${config?.name}</span> ${getLocaleText('is not available for checkout.')}
				</p>
				<hr/>
				<p style="text-align: left; margin: 0.5rem 0 0;" class="muted explanations">
					${eligibilityExplanations}
				<p>
				<button type="button" class="button btn currency-fallback-button ${defaultCurrency ? '' : 'hide'}" data-gateway="${config.gatewayId}" data-currency="${defaultCurrency}">
					${getLocaleText('Update currency to')} <span class="currency">${defaultCurrency}</span>
				</button>
			</div>`,
		);
	}
}

export {initPaymentGatewaySelector};
